import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { useCreateClient } from '@/hooks/use-projects';
import { handleApiError } from '@/lib/api-client';
import type { ProjectClient } from '@/lib/types';

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (client: ProjectClient) => void;
}

export function AddClientDialog({ open, onOpenChange, onCreated }: AddClientDialogProps) {
  const createClient = useCreateClient();
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const close = () => {
    setName('');
    setError('');
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setError('');
    try {
      const client = await createClient.mutateAsync({ name: name.trim() });
      onCreated(client);
      close();
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen ? onOpenChange(true) : close()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New client</DialogTitle>
            <DialogDescription>Create a client without leaving this form.</DialogDescription>
          </DialogHeader>
          <div data-field-container className="space-y-2 py-4">
            <Label htmlFor="new-client-name">Company name *</Label>
            <Input
              id="new-client-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <Button type="submit" disabled={createClient.isPending}>
              {createClient.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create client
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
